<script setup lang="ts">
import { computed } from 'vue';
interface Props {
  type?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  icon?: string;
  href?: string;
  iconColor?: string;
  iconSize?: number | string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'h2',
  iconColor: 'oklch(0 0 0)',
});

// Generate the ID from the href if provided (removing # prefix)
const headingId = computed(() => {
  if (props.href && props.href.startsWith('#')) {
    return props.href.slice(1);
  }
  return props.href;
});

// Calculate icon size based on heading type if not explicitly provided
const iconSize = computed(() => {
  if (props.iconSize) {
    return props.iconSize;
  }

  switch (props.type) {
    case 'h1':
      return 36;
    case 'h2':
      return 32;
    case 'h3':
      return 28;
    case 'h4':
      return 24;
    case 'h5':
      return 20;
    case 'h6':
      return 18;
    default:
      return 32;
  }
});

// Base classes for all heading types
const baseClasses = 'flex items-center text-foreground';

// Type-specific classes - simple and clean
const typeClasses = computed(() => {
  switch (props.type) {
    case 'h1':
      return 'text-3xl md:text-4xl font-bold mt-8 mb-4 gap-3';
    case 'h2':
      return 'text-2xl md:text-3xl font-semibold mt-10 mb-4 gap-2.5';
    case 'h3':
      return 'text-xl md:text-2xl font-semibold mt-8 mb-3 gap-2';
    case 'h4':
      return 'text-lg md:text-xl font-medium mt-6 mb-3 gap-2';
    case 'h5':
      return 'text-base md:text-lg font-medium mt-5 mb-2 gap-1.5';
    case 'h6':
      return 'text-sm md:text-base font-medium mt-4 mb-2 gap-1.5';
    default:
      return 'text-2xl md:text-3xl font-semibold mt-10 mb-4 gap-2.5';
  }
});

// Icon-specific positioning classes
const iconClasses = 'shrink-0 relative';

const combinedClasses = computed(() => `group ${baseClasses} ${typeClasses.value}`);
</script>

<template>
  <component
    :is="type"
    :id="headingId"
    :class="combinedClasses"
  >
    <Icon
      v-if="icon"
      :name="icon"
      :size="iconSize"
      :class="iconClasses"
      :style="{ color: iconColor }"
      aria-hidden="true"
    />
    <span class="flex-1">
      <slot/>
    </span>
    <a
      v-if="href"
      :href="href"
      class="text-muted-foreground hover:text-primary transition-colors ml-2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
      aria-label="Link to this section"
    >
      <Icon name="tabler:link" size="16" aria-hidden="true" />
    </a>
  </component>
</template>
